import { adminRpcDataset } from "../../model/qubes/dataset";
import { adminRpc } from "../../services/admin-rpc-client";
import { createForm } from "./dataset-form";
import { ScheduleDef } from "./schedule-model";
import { ValidationIssue } from "./validation";
import { LookupCache } from "../../services/lookup-cache";

const JOBS = "continuum:jobs";

// The parent-job select's options. Idempotent.
function registerScheduleLookups() {
  LookupCache.register({
    key: JOBS,
    sql: "from job select uid, name",
    valueField: "uid",
    labelField: "name",
  });
}

const scheduleDataset = adminRpcDataset({ cube: "job_schedule", model: ScheduleDef });

// gocron-style expressions the scheduler's Parse() accepts (scheduler/parser_test.go).
const SCHEDULE_EXAMPLES =
  'e.g. "every 5 minutes", "every 60 seconds", "daily at 9:00am", "weekly on monday at 9am", "monthly on day 1 at 9am"';
const SCHEDULE_PREFIX = /^(every |daily at |weekly on |monthly on |\d{4}-\d{2}-\d{2})/i;

function validateSchedule(v: Record<string, unknown>): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  if (!String(v["name"] ?? "").trim()) {
    issues.push({ severity: "error", fields: ["name"], message: "Name is required." });
  }
  if (!v["job_uid"]) {
    issues.push({ severity: "error", fields: ["job_uid"], message: "A job is required." });
  }
  const expr = String(v["schedule_expr"] ?? "").trim();
  if (!expr) {
    issues.push({ severity: "error", fields: ["schedule_expr"], message: "A schedule expression is required." });
  } else if (!SCHEDULE_PREFIX.test(expr)) {
    // non-blocking — the scheduler is the authority; this is just a nudge.
    issues.push({
      severity: "warning",
      fields: ["schedule_expr"],
      message: `"${expr}" doesn't look like a known cadence. ${SCHEDULE_EXAMPLES}`,
    });
  }
  return issues;
}

// Resolve the (single) schedule of one event_kind for a job. A job has 0..N run schedules;
// the Services view is one row per job, so for now we edit the FIRST (create if none).
// Full per-job schedule master/detail is a follow-on (see the form-framework ticket).
// A daemon has at most one restart schedule (idx_job_schedule_one_restart).
async function firstScheduleUidForJob(jobUid: string, eventKind: "run" | "restart"): Promise<string | undefined> {
  const safe = jobUid.replace(/'/g, "''");
  const resp = await adminRpc.query(
    `SELECT uid FROM job_schedule WHERE job_uid = '${safe}' AND event_kind = '${eventKind}' AND status <> 'deleted' ORDER BY created_at LIMIT 1`,
  );
  return resp.rows[0]?.["uid"] as string | undefined;
}

// The one createForm call all schedule entry points share — same sections,
// overrides, and validation whether we're editing by schedule uid, editing a
// job's schedule, or creating a new one.
function openScheduleForm(opts: {
  key?: string;
  title: string;
  defaults?: Record<string, unknown>;
}) {
  registerScheduleLookups();
  createForm(scheduleDataset, {
    key: opts.key,
    title: opts.title,
    submitLabel: "Save schedule",
    defaults: opts.defaults,
    sections: [
      { title: "Identity", fields: [ScheduleDef.name, ScheduleDef.description] },
      { title: "Trigger", fields: [ScheduleDef.job_uid, ScheduleDef.schedule_expr, ScheduleDef.enabled] },
    ],
    overrides: [
      {
        field: ScheduleDef.job_uid,
        item: (i) => ({ ...i, type: "select", options: () => LookupCache.getOptions(JOBS) }),
      },
      {
        field: ScheduleDef.schedule_expr,
        item: (i) => ({ ...i, helpText: `Cadence. ${SCHEDULE_EXAMPLES}` }),
      },
    ],
    validate: validateSchedule,
  });
}

// Job-scoped: edit the job's (first) schedule, creating one if none exists.
// Used by the Jobs/Services view, whose rows are jobs.
export async function showScheduleForm(jobUid: string) {
  const existing = await firstScheduleUidForJob(jobUid, "run");
  openScheduleForm({
    key: existing, // undefined => create a new schedule for this job
    title: existing ? "Edit schedule" : "New schedule",
    // create-mode seeds: bind the schedule to the clicked job and default it on.
    defaults: { job_uid: jobUid, enabled: true },
  });
}

// Schedule-scoped: edit THIS schedule by its own uid. Used by the Schedules view,
// whose rows are individual schedules (a job may have several).
export function showScheduleFormForUid(scheduleUid: string) {
  openScheduleForm({ key: scheduleUid, title: "Edit schedule" });
}

// Create a new schedule; optionally seed field values (e.g. from a Duplicate row).
// The job picker is part of the form, so no job need be pre-bound.
export function showCreateScheduleForm(defaults?: Record<string, unknown>) {
  openScheduleForm({ key: undefined, title: "New schedule", defaults: { enabled: true, ...defaults } });
}

// A daemon job's restart: when the job-runner restarts it. The restart schedule is a
// property of the job, shown only through job.restartSchedule and edited here, so the form
// carries just the time and the on/off switch; the job and the kind are fixed. Turning it
// off keeps the row and stops the restarts.
export async function showRestartForm(jobUid: string, jobName: string) {
  const existing = await firstScheduleUidForJob(jobUid, "restart");
  createForm(scheduleDataset, {
    key: existing,
    title: existing ? `Restart schedule: ${jobName}` : `New restart schedule: ${jobName}`,
    submitLabel: "Save restart schedule",
    defaults: { name: `${jobName} restart`, job_uid: jobUid, event_kind: "restart", enabled: true },
    sections: [
      {
        title: "Restart",
        fields: [ScheduleDef.schedule_expr, ScheduleDef.enabled, ScheduleDef.name, ScheduleDef.job_uid, ScheduleDef.event_kind],
      },
    ],
    overrides: [
      {
        field: ScheduleDef.schedule_expr,
        item: (i) => ({ ...i, label: "Restart at", helpText: 'When to restart, e.g. "daily at 04:00 America/New_York".' }),
      },
      { field: ScheduleDef.name, item: (i) => ({ ...i, hidden: true }) },
      { field: ScheduleDef.job_uid, item: (i) => ({ ...i, hidden: true }) },
      { field: ScheduleDef.event_kind, item: (i) => ({ ...i, hidden: true }) },
    ],
    validate: validateSchedule,
  });
}
